(() => {
  'use strict';

  // The cart stays a plain Shopify cart form: every control works without this file.
  // JavaScript intercepts those same controls, calls the cart API, and re-renders the
  // section through the Section Rendering API so totals, discounts and lines stay in sync.
  const endpoints = (root) => ({
    change: `${(root.dataset.abCartChangeUrl || '/cart/change').replace(/\.js$/, '')}.js`,
    clear: `${(root.dataset.abCartClearUrl || '/cart/clear').replace(/\.js$/, '')}.js`
  });

  /* Strings come from the theme locale files via snippets/agribasket-js-i18n.liquid. */
  const I18N = (window.AgroBucketI18n && window.AgroBucketI18n.strings) || {};
  const tr = (key, fallback, vars) => {
    let out = typeof I18N[key] === 'string' && I18N[key] ? I18N[key] : fallback;
    if (out == null) out = '';
    if (vars) {
      out = String(out).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match);
    }
    return out;
  };

  const init = (root) => {
    if (root.dataset.abCartReady) return;
    root.dataset.abCartReady = '1';

    const sectionId = root.dataset.abCartSection || '';
    const url = endpoints(root);
    let busy = false;

    const status = () => root.querySelector('[data-ab-cart-status]');
    const errorBox = () => root.querySelector('[data-ab-cart-error]');
    const nativeUpdate = () => root.querySelector('[data-ab-cart-update]');

    const announce = (message) => {
      const el = status();
      if (el) el.textContent = message;
    };
    const showError = (message) => {
      const el = errorBox();
      if (!el) return;
      el.textContent = message;
      el.hidden = false;
    };
    const clearError = () => {
      const el = errorBox();
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    };
    const setBusy = (value) => {
      busy = value;
      root.classList.toggle('is-busy', value);
      root.setAttribute('aria-busy', value ? 'true' : 'false');
    };
    const plural = (count) => tr(count === 1 ? 'cart.item' : 'cart.items', '{{ count }} items', { count: count });

    const syncHeaderBadge = (count) => {
      const host = document.querySelector('.ab-cart');
      if (!host) return;
      let badge = host.querySelector('i');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('i');
          host.appendChild(badge);
        }
        badge.textContent = String(count);
      } else if (badge) {
        badge.remove();
      }
    };

    const post = async (endpoint, body) => {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {}),
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      return response.json();
    };

    const render = async (message) => {
      const target = new URL(window.location.href);
      if (sectionId) target.searchParams.set('section_id', sectionId);
      const response = await fetch(target.toString(), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Cart refresh failed with ${response.status}`);
      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ab-cart]');
      if (!fresh) throw new Error('Cart markup was not returned');

      const count = Number(fresh.dataset.abCartCount || 0);
      root.innerHTML = fresh.innerHTML;
      root.dataset.abCartCount = String(count);
      syncHeaderBadge(count);

      const update = nativeUpdate();
      if (update) update.hidden = true;
      clearError();
      if (typeof message === 'function') announce(message(count));
      else if (message) announce(message);
    };

    const commit = async (payload, message, endpoint) => {
      if (busy) return;
      clearError();
      setBusy(true);
      announce(tr('cart.updating', 'Updating your cart\u2026'));
      try {
        await post(endpoint || url.change, payload);
        await render(message);
      } catch (error) {
        const update = nativeUpdate();
        if (update) update.hidden = false;
        showError(tr('cart.update_failed', 'We could not update your cart automatically. Use "Update cart" to save your change, or reload the page.'));
        announce(tr('cart.update_failed_announce', 'Your cart could not be updated automatically.'));
      } finally {
        setBusy(false);
      }
    };

    const quantityInput = (line) => root.querySelector(`[data-ab-cart-qty][data-ab-cart-line="${line}"]`);
    const quantityMessage = (count) => tr('cart.updated', 'Quantity updated. {{ count }} {{ unit }} in your cart.', {
      count: count,
      unit: plural(count)
    });

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const back = target.closest('[data-ab-cart-back]');
      if (back) {
        try {
          const referrer = document.referrer ? new URL(document.referrer) : null;
          if (referrer && referrer.origin === location.origin && history.length > 1) {
            event.preventDefault();
            history.back();
          }
        } catch (error) {
          // Keep the server-rendered back link when the referrer cannot be parsed.
        }
        return;
      }

      const remove = target.closest('[data-ab-cart-remove]');
      if (remove) {
        event.preventDefault();
        commit({ line: Number(remove.dataset.abCartLine), quantity: 0 }, (count) => (
          count === 0
            ? tr('cart.item_removed_empty', 'Item removed. Your cart is empty.')
            : tr('cart.item_removed', 'Item removed. {{ count }} {{ unit }} in your cart.', { count: count, unit: plural(count) })
        ));
        return;
      }

      const step = target.closest('[data-ab-cart-step]');
      if (step) {
        const line = step.dataset.abCartLine;
        const input = quantityInput(line);
        const current = input ? Number(input.value) || 0 : 0;
        const next = Math.max(1, current + Number(step.dataset.abCartDelta || 0));
        if (next === current) return;
        commit({ line: Number(line), quantity: next }, quantityMessage);
      }
    });

    root.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-ab-cart-qty]')) return;

      const previous = Number(input.dataset.abCartValue) || 0;
      const quantity = Number.parseInt(input.value, 10);
      if (!Number.isFinite(quantity) || quantity < 0) {
        input.value = String(previous);
        showError(tr('cart.invalid_quantity', 'Enter a whole number of 0 or more. Enter 0 to remove the item.'));
        return;
      }
      if (quantity === previous) return;
      commit({ line: Number(input.dataset.abCartLine), quantity }, quantityMessage);
    });

    root.addEventListener('submit', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest('[data-ab-cart-clear-form]')) return;
      event.preventDefault();
      commit({}, tr('cart.cleared', 'Cart cleared. Your cart is empty.'), url.clear);
    });
  };

  document.querySelectorAll('[data-ab-cart]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-ab-cart]').forEach(init);
  });
})();
